/**
 * Anglais des 16 landings thématiques.
 *
 * Mêmes règles que `landings.nl.js` : UNIQUEMENT du texte, superposé au
 * français par indice (cf. `landingTheme.js`). Aucun chiffre ici — score,
 * montants et pourcentages viennent tous de `landings.js`, écrits une fois.
 *
 * RÉFÉRENCES LÉGALES — le droit belge n'a pas de version anglaise
 * officielle. Les intitulés sont donc rendus en anglais courant en gardant
 * l'ancre vérifiable telle quelle : la DATE et le NUMÉRO d'article ne
 * bougent pas (« Royal Decree of 12 October 2010 », « Art. VI.43 CEL »).
 * C'est ce couple date+article qui permet de retrouver le texte belge
 * d'origine, quelle que soit la langue de lecture.
 *
 * « CDE » (Code de droit économique) est rendu « CEL » (Code of Economic
 * Law), la forme employée dans les traductions de travail belges.
 */
export const LANDING_THEMES_EN = {
  'pv': {
    badge: '🚗 Tickets & speeding fines',
    h1: ['Got a ticket?', 'Don’t pay before you know.'],
    dropzone: 'Drop your ticket here, or click',
    checksTitle: 'What Archer checks on your ticket',
    checksIntro: 'One of these defects alone can be enough to bring the ticket down',
    checks: [
      ['The speed camera’s calibration certificate', '· expired, and the ticket is fragile'],
      ['The zone’s signage', '· missing or not repeated, that is contestable'],
      ['The tolerance margin applied', '· it is mandatory'],
      ['The mandatory particulars of the report', '· date, time and place of the finding, identity of the officer'],
      ['The identification of the driver', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on Belgian road traffic legislation. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your ticket the way it does.',
    bandLead: 'The AI analyses your ticket in 7 passes.',
    faqTitle: 'Your questions about tickets',
    faqs: [
      { q: 'Do I have to pay right away?', a: 'No. And paying too quickly can count as acceptance. Scan it first: you will know whether your ticket can be contested and how long you have.' },
      { q: 'Is contesting it legal?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your ticket.' },
      { q: 'And if my ticket is uncontestable?', a: 'Archer tells you straight. Paying quickly may then be your best option, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your ticket has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my ticket · Free',
    proof: {
      kicker: '● TICKET CONTESTABLE',
      headline: 'Three exploitable flaws.',
      summary: 'Your ticket has real procedural defects: the speed camera’s calibration certificate is not mentioned, the tolerance margin appears misapplied, and the zone’s signage is not substantiated.',
      kpis: [
        { label: 'Amount claimed', sub: 'fine + costs' },
        { label: 'If the ticket falls', sub: 'you pay nothing' },
        { label: 'Deadline to contest', value: '12 days', sub: 'from your document' },
      ],
      args: [
        { title: 'The annual verification certificate for the speed meter is not produced', basis: 'Royal Decree of 12 October 2010 · approval and annual inspection', tag: 'STRONG' },
        { title: 'The technical tolerance margin does not appear to be applied', basis: 'RD 12/10/2010 · the recorded speed must be corrected', tag: 'STRONG' },
        { title: 'The zone’s signage is not established in the report', basis: 'Art. 62 Act of 16 March 1968 · evidentiary force of the report', tag: 'MODERATE' },
      ],
      letter: [
        'The request for the speed camera’s calibration certificate',
        'The recalculation of the speed after the tolerance margin',
        'The formal challenge within the legal deadline, by registered mail',
        'The request for a reasoned dismissal',
      ],
      cta: 'Analyse my ticket · Free',
      projection: [
        { label: 'Cancelled' },
        { label: 'Reduced' },
        { label: 'Upheld' },
      ],
      note: 'And if your ticket holds up, Archer tells you straight: you pay quickly, with no extra costs.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'huissier': {
    badge: '📨 Bailiff’s letter',
    h1: ['A bailiff writing to you?', 'Check what they can actually do.'],
    dropzone: 'Drop your letter here, or click',
    checksTitle: 'What Archer checks on your letter',
    checksIntro: 'One of these defects alone can be enough to bring the claim down',
    checks: [
      ['The nature of the letter', '· amicable or judicial, the difference is enormous'],
      ['The costs claimed', '· often inflated, contestable'],
      ['The limitation period of the debt', '· a debt too old is no longer enforceable'],
      ['The mandatory particulars of the formal notice', '· identity of the creditor, origin and breakdown of the debt'],
      ['The real possibility of seizure', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on Belgian debt-recovery legislation. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your formal notice the way it does.',
    bandLead: 'The AI analyses your letter in 7 passes.',
    faqTitle: 'Your questions about bailiffs',
    faqs: [
      { q: 'Do I have to pay right away?', a: 'No. And paying too quickly can count as acceptance. Scan it first: you will know whether the claim can be contested and how long you have.' },
      { q: 'Is contesting it legal?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your formal notice.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Paying quickly may then be your best option, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['This letter has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my letter · Free',
    proof: {
      kicker: '● AMICABLE FORMAL NOTICE',
      headline: 'They cannot seize anything. Not yet.',
      summary: 'This letter is an amicable formal notice, not judicial proceedings. No seizure is possible without a judgment, and the costs claimed exceed the legal scale.',
      kpis: [
        { label: 'Amount claimed', sub: 'including recovery costs' },
        { label: 'Contestable costs', sub: 'over the legal scale' },
        { label: 'Deadline to respond', value: '8 days', sub: 'before the reminder' },
      ],
      args: [
        { title: 'The recovery costs exceed the legal ceiling', basis: 'Act of 20 December 2002 on amicable debt recovery', tag: 'STRONG' },
        { title: 'No proof of the original debt is attached', basis: 'Art. 1315 former Civil Code · burden of proof', tag: 'STRONG' },
        { title: 'The limitation period may have been reached', basis: 'Art. 2262bis former Civil Code', tag: 'MODERATE' },
      ],
      letter: [
        'The challenge to the costs above the legal scale',
        'The request for proof of the original debt',
        'The reminder that no seizure is possible without a judgment',
        'The request for a payment plan if the debt is owed',
      ],
      cta: 'Analyse my letter · Free',
      projection: [
        { label: 'Costs reduced' },
        { label: 'Debt confirmed' },
        { label: 'No reply' },
      ],
      note: 'And if the debt really is yours, Archer tells you: you will know what to pay, at the right price.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'caution': {
    badge: '🏠 Rental deposit',
    h1: ['Landlord keeping your deposit?', 'They may have no right to.'],
    dropzone: 'Drop your document here, or click',
    checksTitle: 'What Archer checks on your deposit',
    checksIntro: 'One of these defects alone can be enough to bring the withholding down',
    checks: [
      ['Normal wear versus damage', '· the legal line is precise'],
      ['The exit inventory of fixtures', '· compared point by point with the entry one'],
      ['The supporting documents for the withholding', '· invoices or quotes are mandatory'],
      ['The mandatory particulars of the inventory', '· drawn up jointly, dated and signed by both parties'],
      ['The proportionality of the withholding', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on Belgian tenancy legislation. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your inventory of fixtures the way it does.',
    bandLead: 'The AI analyses your case in 7 passes.',
    faqTitle: 'Your questions about the deposit',
    faqs: [
      { q: 'Can they keep my deposit?', a: 'Only for substantiated damage, backed by invoices or quotes, never for normal wear. Scan it first: you will know whether the withholding on your rental deposit can be contested and how long you have.' },
      { q: 'Am I entitled to contest the withholding?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your exit inventory.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Letting it go may then be the right call, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your deposit has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my deposit · Free',
    proof: {
      kicker: '● WITHHOLDING CONTESTABLE',
      headline: '1 850 € that belong to you.',
      summary: 'Comparing the entry and exit inventories shows normal wear on almost every point withheld. No invoice justifies the amount deducted.',
      kpis: [
        { label: 'Deposit withheld', sub: 'out of 2 100 € paid' },
        { label: 'What you recover', sub: 'no supporting document' },
        { label: 'Deadline to act', value: '2 months', sub: 'before the limitation period' },
      ],
      args: [
        { title: 'The withholdings concern normal wear, which cannot be charged', basis: 'Art. 1730 former Civil Code · obligations on leaving', tag: 'STRONG' },
        { title: 'No invoice or quote justifies the amount withheld', basis: 'Act of 20 February 1991, art. 10 · written proof', tag: 'STRONG' },
        { title: 'The deadline to return the balance has passed', basis: 'Settled case law of the justices of the peace', tag: 'MODERATE' },
      ],
      letter: [
        'The request for full return within 15 days',
        'The point-by-point challenge to the inventory',
        'The request for the missing supporting documents',
        'The reminder of the interest due on the blocked account',
      ],
      cta: 'Analyse my deposit · Free',
      projection: [
        { label: 'Full return' },
        { label: 'Partial return' },
        { label: 'Refusal' },
      ],
      note: 'And if the withholdings are justified, Archer tells you clearly: you will know exactly why.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'licenciement': {
    badge: '💼 Dismissal & termination',
    h1: ['Just been dismissed?', 'Check what you are owed before you sign.'],
    dropzone: 'Drop your letter or your C4 here, or click',
    checksTitle: 'What Archer checks on your dismissal',
    checksIntro: 'One of these points alone can be worth several thousand euros',
    checks: [
      ['The notice period respected', '· the legal length according to your seniority'],
      ['The compensatory indemnity', '· the exact calculation if notice is not worked'],
      ['The grounds invoked', '· a manifestly unreasonable dismissal gives rise to compensation'],
      ['The mandatory particulars of the notice letter', '· start and length of the notice, legal form respected'],
      ['Compliance with the CBA 109 procedure', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on Belgian employment legislation. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your C4 the way it does.',
    bandLead: 'The AI analyses your case in 7 passes.',
    faqTitle: 'Your questions about dismissal',
    faqs: [
      { q: 'What am I entitled to?', a: 'To the notice or its compensatory indemnity, to the departure holiday pay, and to the reasons for your dismissal if you ask for them. Scan it first: you will know whether your dismissal letter can be contested and how long you have.' },
      { q: 'Am I entitled to claim?', a: 'Yes, it is a right. Claiming follows a precise procedure: Archer hands it to you, tailored to your C4.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Letting it go may then be the right call, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to claim for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your case has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my C4 · Free',
    proof: {
      kicker: '● COMPENSATION OWED',
      headline: 'They still owe you 2 400 €.',
      summary: 'Your three-month notice was only partly worked according to your payslips. The balance appears nowhere. The departure holiday pay was not paid out either.',
      kpis: [
        { label: 'Balance paid', sub: 'to date' },
        { label: 'What they owe you', sub: 'full calculation' },
        { label: 'Deadline to act', value: '34 days', sub: 'before the limitation period' },
      ],
      args: [
        { title: 'Incomplete notice · 6 weeks not covered', basis: 'Act of 3 July 1978, art. 37 · calculation according to seniority', tag: 'STRONG' },
        { title: 'Departure holiday pay not paid out', basis: 'Royal Decree of 30 March 1967 · mandatory payment at the end of the contract', tag: 'STRONG' },
        { title: 'No formal statement of the reasons for dismissal', basis: 'CBA no. 109 · right to a statement of reasons', tag: 'MODERATE' },
      ],
      letter: [
        'The itemised request for the missing notice balance',
        'The request for the departure holiday pay',
        'The request for a formal statement of the reasons for dismissal',
        'The deadline to reply and what follows silence',
      ],
      cta: 'Analyse my C4 · Free',
      projection: [
        { label: 'Fully obtained' },
        { label: 'Partly obtained' },
        { label: 'Refusal' },
      ],
      note: 'And if your final settlement is correct, Archer confirms it, no pointless claim.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'facture': {
    badge: '🧾 Contested invoice',
    h1: ['An invoice that does not add up?', 'Don’t pay before you have checked.'],
    dropzone: 'Drop your invoice here, or click',
    checksTitle: 'What Archer checks on your invoice',
    checksIntro: 'One of these defects alone can be enough to bring the invoice down',
    checks: [
      ['The hidden fees', '· activation, termination, services never asked for'],
      ['Consistency with your contract', '· does the price charged match what you signed'],
      ['Unauthorised SEPA direct debits', '· automatic refund within 8 weeks'],
      ['The mandatory particulars of the invoice', '· identity of the provider, breakdown of services, VAT'],
      ['The indexation applied', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on the Belgian Code of Economic Law. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your invoice the way it does.',
    bandLead: 'The AI analyses your invoice in 7 passes.',
    faqTitle: 'Your questions about invoices',
    faqs: [
      { q: 'Do I have to pay right away?', a: 'No. And paying too quickly can count as acceptance. Scan it first: you will know whether your invoice can be contested and how long you have.' },
      { q: 'Is contesting it legal?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your invoice.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Paying quickly may then be your best option, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your invoice has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my invoice · Free',
    proof: {
      kicker: '● INVOICE CONTESTABLE',
      headline: '247 € of unjustified fees.',
      summary: 'Your operator is charging termination fees although your initial 24-month contract expired three months ago: termination should have been free.',
      kpis: [
        { label: 'Amount claimed', sub: 'termination fees' },
        { label: 'If the invoice falls', sub: 'you pay nothing' },
        { label: 'Deadline to contest', value: '30 days', sub: 'from your document' },
      ],
      args: [
        { title: 'Initial contract expired 3 months ago · free termination owed', basis: 'Art. 111/3 §3, Act of 13 June 2005', tag: 'STRONG' },
        { title: 'Termination fees not itemised on the invoice', basis: 'Art. VI.83 CEL · unfair terms', tag: 'STRONG' },
        { title: 'No signed agreement for the automatic renewal', basis: 'Art. VI.45 CEL · prior information', tag: 'MODERATE' },
      ],
      letter: [
        'The formal challenge to the termination fees',
        'The request for a detailed justification of the amount',
        'The request for a refund within 30 days',
        'The formal notice in case of silence',
      ],
      cta: 'Analyse my invoice · Free',
      projection: [
        { label: 'Full refund' },
        { label: 'Partial refund' },
        { label: 'Refusal' },
      ],
      note: 'And if the fees are justified, Archer tells you clearly: you will know exactly what you are paying for.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'arnaque': {
    badge: '🛒 Online purchase',
    h1: ['Never delivered, never refunded?', 'You have more rights than you think.'],
    dropzone: 'Drop your proof of purchase here, or click',
    checksTitle: 'What Archer checks on your order',
    checksIntro: 'One of these points alone can be enough to get your refund',
    checks: [
      ['The legal delivery deadline', '· 30 days maximum, beyond that you can demand a refund'],
      ['The right of withdrawal', '· 14 days, even without a reason'],
      ['The legal guarantee of conformity', '· 2 years, even if the seller does not mention it'],
      ['The mandatory particulars of the order confirmation', '· confirmation of the contract on a durable medium, total price'],
      ['The seller’s verifiable identity', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on the Belgian Code of Economic Law. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your order the way it does.',
    bandLead: 'The AI analyses your case in 7 passes.',
    faqTitle: 'Your questions about online scams',
    faqs: [
      { q: 'Can I be refunded?', a: 'Yes if the seller did not deliver within 30 days, and in any case within the 14-day withdrawal window, even without a reason. Scan it first: you will know whether your order gives rise to a refund and how long you have.' },
      { q: 'Am I entitled to demand a refund?', a: 'Yes, it is a right. Claiming follows a precise procedure: Archer hands it to you, tailored to your order and your proof of purchase.' },
      { q: 'And if the seller has vanished?', a: 'Archer tells you straight. A bank chargeback does not depend on them, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to claim for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your case has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my order · Free',
    proof: {
      kicker: '● REFUND OWED',
      headline: 'The seller is in the wrong, not you.',
      summary: 'Your order was to be delivered within 30 days: 52 days have passed with no news. The right to rescind the contract is open, with a full refund.',
      kpis: [
        { label: 'Amount claimed', sub: 'order not delivered' },
        { label: 'What you recover', sub: 'full refund' },
        { label: 'Deadline to act', value: 'Immediate', sub: 'legal deadline exceeded' },
      ],
      args: [
        { title: 'Delivery not made after 52 days', basis: 'Art. VI.43 CEL · obligation to deliver within 30 days', tag: 'STRONG' },
        { title: 'No reply from the seller to the reminders', basis: 'Art. VI.44 CEL · right to rescind the contract', tag: 'STRONG' },
        { title: 'No verifiable legal particulars on the site', basis: 'Art. VI.45 CEL · information obligation', tag: 'MODERATE' },
      ],
      letter: [
        'The formal notice to deliver within 8 days',
        'The request for rescission and a full refund',
        'The reference to the applicable articles of the CEL',
        'The bank chargeback procedure in case of silence',
      ],
      cta: 'Analyse my order · Free',
      projection: [
        { label: 'Refund obtained' },
        { label: 'Partial refund' },
        { label: 'No reply' },
      ],
      note: 'And if your order is still within the deadline, Archer tells you, no pointless step.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'bail': {
    badge: '🏠 Tenancy & housing',
    h1: ['Landlord pushing you out?', 'Check whether they are allowed to.'],
    dropzone: 'Drop your letter here, or click',
    checksTitle: 'What Archer checks on your tenancy',
    checksIntro: 'One of these defects alone can be enough to bring the notice down',
    checks: [
      ['The notice period', '· legal length and permitted grounds according to the region'],
      ['The form of the notice', '· registered mail is mandatory, otherwise void'],
      ['The rent increase', '· correct indexation, free of unfair terms'],
      ['The mandatory particulars of the written lease', '· rent, term, description of the property and registration'],
      ['The inventory: wear versus damage', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on regional tenancy legislation. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your lease the way it does.',
    bandLead: 'The AI analyses your lease in 7 passes.',
    faqTitle: 'Your questions about the lease',
    faqs: [
      { q: 'Do I have to leave right away?', a: 'No. And leaving too quickly can count as accepting the notice. Scan it first: you will know whether the notice you received can be contested and how long you have.' },
      { q: 'Is contesting it legal?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your notice and your lease.' },
      { q: 'And if the notice is perfectly valid?', a: 'Archer tells you straight. You will then know the date you really have to leave, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your notice has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my notice · Free',
    proof: {
      kicker: '● NOTICE IRREGULAR',
      headline: 'The notice does not meet the legal form.',
      summary: 'The notice given by your landlord does not state the ground required for early termination, and the period served is shorter than the legal minimum applicable to your lease.',
      kpis: [
        { label: 'Period served', value: '60 days', sub: 'demanded by the letter' },
        { label: 'Period actually owed', value: '6 months', sub: 'legal minimum' },
        { label: 'Deadline to respond', value: '15 days', sub: 'before it takes effect' },
      ],
      args: [
        { title: 'Ground for early termination not substantiated in the letter', basis: 'Residential Tenancy Act, art. 3 §5 · restricted grounds', tag: 'STRONG' },
        { title: 'Notice period shorter than the legal minimum', basis: 'Art. 3 §2 · minimum length according to the ground', tag: 'STRONG' },
        { title: 'No dispatch by registered mail', basis: 'Art. 3 §1 · mandatory form, otherwise void', tag: 'MODERATE' },
      ],
      letter: [
        'The formal challenge to the irregular notice',
        'The request that the legal minimum period be respected',
        'The reminder that a defect of form makes it void',
        'The next steps in case of disagreement',
      ],
      cta: 'Analyse my notice · Free',
      projection: [
        { label: 'Notice cancelled' },
        { label: 'Period renegotiated' },
        { label: 'Notice upheld' },
      ],
      note: 'And if the notice is perfectly valid, Archer tells you, you can organise yourself calmly.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'assurance': {
    badge: '🛡️ Insurance dispute',
    h1: ['Insurer refusing to pay?', 'Have their refusal checked.'],
    dropzone: 'Drop the refusal letter here, or click',
    checksTitle: 'What Archer checks on your refusal',
    checksIntro: 'One of these defects alone can be enough to bring the refusal down',
    checks: [
      ['The reasons given for the refusal', '· they must be precise, not just a clause quoted'],
      ['The clause invoked', '· its validity and its actual application'],
      ['The handling time', '· an insurer who drags it out can be put on formal notice'],
      ['The mandatory particulars of the contract', '· an exclusion absent from the policy cannot be held against you'],
      ['The amount offered against the real value', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on the Belgian Insurance Act. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your refusal letter the way it does.',
    bandLead: 'The AI analyses your case in 7 passes.',
    faqTitle: 'Your questions about insurance disputes',
    faqs: [
      { q: 'Are they allowed to refuse?', a: 'Only on an exclusion provided for in the contract, and by giving precise reasons: a quoted clause is not enough. Scan it first: you will know whether the refusal letter can be contested and how long you have.' },
      { q: 'Is contesting it legal?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your refusal letter.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Letting it go may then be the right call, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your appeal has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my refusal · Free',
    proof: {
      kicker: '● REFUSAL CONTESTABLE',
      headline: 'The clause invoked does not apply.',
      summary: 'The insurer refuses your claim by invoking an exclusion for lack of maintenance, but no expert report backs it up. This refusal rests on an assertion, not on evidence.',
      kpis: [
        { label: 'Amount of the claim', sub: 'estimated value' },
        { label: 'What you recover', sub: 'if it succeeds' },
        { label: 'Deadline to contest', value: '1 month', sub: 'before the limitation period' },
      ],
      args: [
        { title: 'Refusal not supported by a documented expert assessment', basis: 'Act of 4 April 2014, art. 62 · duty to state reasons', tag: 'STRONG' },
        { title: 'No evidence of the lack of maintenance invoked', basis: 'Art. 1315 former Civil Code · burden of proof', tag: 'STRONG' },
        { title: 'Handling time for the file exceeded', basis: 'Art. 22 Act of 4 April 2014', tag: 'MODERATE' },
      ],
      letter: [
        'The challenge to the refusal and the request for the expert report',
        'The request for an independent counter-assessment',
        'The formal notice to re-examine the file',
        'The reminder of the avenues of appeal (insurance ombudsman)',
      ],
      cta: 'Analyse my refusal · Free',
      projection: [
        { label: 'Compensation obtained' },
        { label: 'Partial compensation' },
        { label: 'Refusal upheld' },
      ],
      note: 'And if the refusal is well founded, Archer explains it clearly, so you know where you stand.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'telecom': {
    badge: '📱 Telecom · Proximus, Orange, Telenet',
    h1: ['Telecom bill gone up?', 'They count on you not reading it.'],
    dropzone: 'Drop your invoice here, or click',
    checksTitle: 'What Archer checks on your telecom invoice',
    checksIntro: 'One of these defects alone can be enough to bring the fees down',
    checks: [
      ['The termination fees', '· often charged although the commitment has ended'],
      ['Options never subscribed to', '· appearing with no signed agreement'],
      ['The unilateral price increase', '· you have a right to terminate free of charge'],
      ['The mandatory particulars of the telecom invoice', '· breakdown of items, rates and ancillary fees'],
      ['Consistency with your original contract', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on the Belgian Code of Economic Law and telecom act. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your telecom invoice the way it does.',
    bandLead: 'The AI analyses your invoice in 7 passes.',
    faqTitle: 'Your questions about telecom disputes',
    faqs: [
      { q: 'Do I have to pay right away?', a: 'No. And paying too quickly can count as acceptance. Scan it first: you will know whether your telecom invoice can be contested and how long you have.' },
      { q: 'Is contesting it legal?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your telecom invoice.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Paying quickly may then be your best option, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your invoice has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my invoice · Free',
    proof: {
      kicker: '● FEES CONTESTABLE',
      headline: '247 € your operator owes you.',
      summary: 'Termination fees are being charged although your commitment period ended three months ago, and options appear on your invoice with no signed agreement to justify them.',
      kpis: [
        { label: 'Amount claimed', sub: 'fees + options' },
        { label: 'What you recover', sub: 'if the challenge succeeds' },
        { label: 'Deadline to contest', value: '30 days', sub: 'from the invoice' },
      ],
      args: [
        { title: 'Termination fees charged after the end of the commitment', basis: 'Art. 111/3 §3, Act of 13 June 2005 · free of charge after the minimum period', tag: 'STRONG' },
        { title: 'Options charged with no proof of subscription', basis: 'Art. VI.45 CEL · information obligation and prior agreement', tag: 'STRONG' },
        { title: 'Renewal clause not brought to your attention', basis: 'Art. VI.83 CEL · unfair terms', tag: 'MODERATE' },
      ],
      letter: [
        'The detailed challenge, item by item',
        'The request for proof that the options were subscribed to',
        'The request for a refund within 30 days',
        'Referral to the Telecom Ombudsman Service in case of refusal',
      ],
      cta: 'Analyse my telecom invoice · Free',
      projection: [
        { label: 'Full refund' },
        { label: 'Goodwill gesture' },
        { label: 'Refusal' },
      ],
      note: 'And if the fees are owed, Archer tells you clearly: you pay what is fair, nothing more.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'energie': {
    badge: '⚡ Energy · annual settlement',
    h1: ['1 480 € of settlement?', 'Check before you pay.'],
    dropzone: 'Drop your invoice here, or click',
    checksTitle: 'What Archer checks on your energy invoice',
    checksIntro: 'One of these defects alone can be enough to bring part of the amount down',
    checks: [
      ['Estimated versus actual meter readings', '· an invoice must reflect your real consumption'],
      ['The period invoiced', '· beyond 5 years, the consumption is time-barred'],
      ['The rate applied', '· in line with your contract or changed unilaterally'],
      ['The mandatory particulars of the energy invoice', '· supply point, exact period and rate applied'],
      ['The ancillary fees added', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on Belgian consumer and energy law. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your energy invoice the way it does.',
    bandLead: 'The AI analyses your invoice in 7 passes.',
    faqTitle: 'Your questions about energy invoices',
    faqs: [
      { q: 'Do I have to pay right away?', a: 'No. And paying too quickly can count as acceptance. Scan it first: you will know whether your settlement invoice can be contested and how long you have.' },
      { q: 'Is contesting it legal?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your energy invoice.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Paying quickly may then be your best option, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your settlement has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my invoice · Free',
    proof: {
      kicker: '● SETTLEMENT CONTESTABLE',
      headline: '1 480 € to look at closely.',
      summary: 'Your annual settlement rests on estimated meter readings, not actual ones. Part of the period invoiced falls outside the window in which a supplier may claim consumption.',
      kpis: [
        { label: 'Amount claimed', sub: 'annual settlement' },
        { label: 'Potentially cancellable', sub: 'time-barred period' },
        { label: 'Deadline to contest', value: '30 days', sub: 'from the invoice' },
      ],
      args: [
        { title: 'Billing based on estimated readings, never actually taken', basis: 'Obligation to bill on real consumption', tag: 'STRONG' },
        { title: 'Part of the period claimed is time-barred', basis: 'Art. 2277 former Civil Code · five-year limitation on consumption', tag: 'STRONG' },
        { title: 'No payment plan offered before the formal notice', basis: 'Regulated practice of the energy sector', tag: 'MODERATE' },
      ],
      letter: [
        'The request for the breakdown of actual versus estimated readings',
        'The challenge to the time-barred period',
        'The request for a staggered payment plan',
        'Referral to the energy ombudsman in case of deadlock',
      ],
      cta: 'Analyse my energy invoice · Free',
      projection: [
        { label: 'Invoice corrected' },
        { label: 'Payment plan' },
        { label: 'Upheld' },
      ],
      note: 'And if the consumption is real, Archer tells you, and helps you get it spread out rather than a formal notice.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'sepa': {
    badge: '🏦 Unauthorised SEPA direct debits',
    h1: ['A debit you do not recognise?', 'You can demand a refund.'],
    dropzone: 'Drop your statement here, or click',
    checksTitle: 'What Archer checks on your direct debits',
    checksIntro: 'One of these points alone can be enough to get your money back',
    checks: [
      ['The existence of a signed mandate', '· with no mandate, the debit is unauthorised'],
      ['The 8-week window', '· refund on request, with no justification needed'],
      ['Unauthorised debits', '· refundable for up to 13 months'],
      ['The mandatory particulars of the direct debit mandate', '· unique reference, creditor identifier, date and signature'],
      ['The creditor’s real identity', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on Belgian and European payment services law. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your statement the way it does.',
    bandLead: 'The AI analyses your statement in 7 passes.',
    faqTitle: 'Your questions about direct debits',
    faqs: [
      { q: 'Can I get my money back?', a: 'Yes, with no justification needed within 8 weeks, and for up to 13 months if the debit was not authorised. Scan it first: you will know whether your debits can be contested and how long you have.' },
      { q: 'Am I entitled to ask for a refund?', a: 'Yes, it is a right. The request follows a precise procedure: Archer hands it to you, tailored to your statement and the direct debit mandate.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Letting it go may then be the right call, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to claim for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your debits have deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my direct debits · Free',
    proof: {
      kicker: '● REFUND AS OF RIGHT',
      headline: 'Your bank has to refund you.',
      summary: 'SEPA direct debits appear on your account with no traceable signed mandate. For an authorised SEPA direct debit, you have 8 weeks to ask for a refund with no justification.',
      kpis: [
        { label: 'Amount debited', sub: 'over 12 months' },
        { label: 'Immediately recoverable', sub: 'within the 8-week window' },
        { label: 'Deadline to observe', value: '8 weeks', sub: 'per debit' },
      ],
      args: [
        { title: 'No signed direct debit mandate can be produced', basis: 'Regulation (EU) 260/2012 · the mandate is mandatory', tag: 'STRONG' },
        { title: 'Right to a refund with no justification within 8 weeks', basis: 'Book VII CEL · payment services', tag: 'STRONG' },
        { title: 'Unauthorised debits: refund possible for up to 13 months', basis: 'Book VII CEL · unauthorised transactions', tag: 'MODERATE' },
      ],
      letter: [
        'The request for an immediate refund from your bank',
        'The challenge to the debits with the creditor',
        'The request that the signed SEPA mandate be produced',
        'The formal revocation of the mandate for the future',
      ],
      cta: 'Analyse my direct debits · Free',
      projection: [
        { label: 'Refund obtained' },
        { label: 'Partial refund' },
        { label: 'Dispute to pursue' },
      ],
      note: 'And if the mandate does exist, Archer tells you, and explains how to revoke it properly.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'vitesse': {
    badge: '📷 Speeding · radar, lidar, 30 zone',
    h1: ['Flashed?', 'Don’t pay before you know.'],
    dropzone: 'Drop your ticket here, or click',
    checksTitle: 'What Archer checks on your speeding ticket',
    checksIntro: 'One of these defects alone can be enough to bring the ticket down',
    checks: [
      ['The device’s calibration certificate', '· expired, and the ticket becomes fragile'],
      ['The zone’s signage', '· missing or not repeated, that is contestable, and often overlooked'],
      ['The tolerance margin applied', '· deducted from the reading, it is mandatory'],
      ['The mandatory particulars of the report', '· device used, speed measured and speed recorded'],
      ['The deadline for sending the copy of the report', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on Belgian road traffic legislation. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your ticket the way it does.',
    bandLead: 'The AI analyses your ticket in 7 passes.',
    faqTitle: 'Your questions about speeding tickets',
    faqs: [
      { q: 'Do I have to pay right away?', a: 'No. And paying too quickly can count as acceptance. Scan it first: you will know whether your ticket can be contested and how long you have.' },
      { q: 'Is contesting it legal?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your ticket.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Paying quickly may then be your best option, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your ticket has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my ticket · Free',
    proof: {
      kicker: '● TICKET CONTESTABLE',
      headline: 'Three exploitable flaws.',
      summary: 'Your ticket has real procedural defects: the speed camera’s calibration certificate is not mentioned, the tolerance margin appears misapplied, and the zone’s signage is not substantiated.',
      kpis: [
        { label: 'Amount claimed', sub: 'fine + costs' },
        { label: 'If the ticket falls', sub: 'you pay nothing' },
        { label: 'Deadline to contest', value: '12 days', sub: 'from your document' },
      ],
      args: [
        { title: 'The annual verification certificate for the speed meter is not produced', basis: 'Royal Decree of 12 October 2010 · approval and annual inspection', tag: 'STRONG' },
        { title: 'The technical tolerance margin does not appear to be applied', basis: 'RD 12/10/2010 · the recorded speed must be corrected', tag: 'STRONG' },
        { title: 'The zone’s signage is not established in the report', basis: 'Art. 62 Act of 16 March 1968 · evidentiary force of the report', tag: 'MODERATE' },
      ],
      letter: [
        'The request for the speed camera’s calibration certificate',
        'The recalculation of the speed after the tolerance margin',
        'The formal challenge within the legal deadline, by registered mail',
        'The request for a reasoned dismissal',
      ],
      cta: 'Analyse my ticket · Free',
      projection: [
        { label: 'Cancelled' },
        { label: 'Reduced' },
        { label: 'Upheld' },
      ],
      note: 'And if your ticket holds up, Archer tells you straight: you pay quickly, with no extra costs.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'telephone-volant': {
    badge: '📵 Phone at the wheel',
    h1: ['Booked for your phone?', 'Check what the report actually proves.'],
    dropzone: 'Drop your ticket here, or click',
    checksTitle: 'What Archer checks on your report',
    checksIntro: 'One of these defects alone can be enough to bring the report down',
    checks: [
      ['The precise description of the use observed', '· holding ≠ phoning: the distinction is a legal one'],
      ['The physical evidence attached', '· photo, video, or a bare assertion?'],
      ['The state of the vehicle', '· stopped in a permitted place, and the offence falls away'],
      ['The mandatory particulars of the report', '· precise description of the facts observed, date and place'],
      ['The degree of offence recorded', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on the Belgian Highway Code. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your report the way it does.',
    bandLead: 'The AI analyses your report in 7 passes.',
    faqTitle: 'Your questions about phones at the wheel',
    faqs: [
      { q: 'Do I have to pay right away?', a: 'No. And paying too quickly can count as acceptance. Scan it first: you will know whether your report can be contested and how long you have.' },
      { q: 'Is contesting it legal?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your report.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Paying quickly may then be your best option, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your report has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my report · Free',
    proof: {
      kicker: '● FINDING CONTESTABLE',
      headline: 'Holding is not phoning.',
      summary: 'The report asserts that the phone was used without describing precisely what was observed. Yet the offence presupposes actual use of the device held in the hand, not its mere presence.',
      kpis: [
        { label: 'Amount claimed', sub: '2nd degree fine' },
        { label: 'If the report falls', sub: 'you pay nothing' },
        { label: 'Deadline to contest', value: '15 days', sub: 'from the notification' },
      ],
      args: [
        { title: 'The report does not describe precisely the use observed', basis: 'Art. 8.4 Highway Code · use of the device held in the hand', tag: 'STRONG' },
        { title: 'No photo or physical evidence supports the finding', basis: 'Art. 62 Act of 16 March 1968 · evidentiary force limited to the findings', tag: 'STRONG' },
        { title: 'The vehicle may have been stopped in a permitted place', basis: 'Art. 8.4 · the offence presupposes driving', tag: 'MODERATE' },
      ],
      letter: [
        'The request for the full report and its annexes',
        'The challenge to the materiality of the facts observed',
        'The request for access to any images',
        'The request for a reasoned dismissal',
      ],
      cta: 'Analyse my report · Free',
      projection: [
        { label: 'Cancelled' },
        { label: 'Reduced' },
        { label: 'Upheld' },
      ],
      note: 'And if the finding is solid, Archer tells you: you pay quickly, with no additional costs.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'jamais-livre': {
    badge: '📦 Never delivered, never refunded',
    h1: ['Vinted, fake site, ghost seller?', 'You have more rights than you think.'],
    dropzone: 'Drop your proof of purchase here, or click',
    checksTitle: 'What Archer checks on your order',
    checksIntro: 'One of these points alone can be enough to get your refund',
    checks: [
      ['The legal delivery deadline', '· 30 days maximum, beyond that you can demand a refund'],
      ['The right of withdrawal', '· 14 days, even without a reason'],
      ['The legal guarantee of conformity', '· 2 years, even if the seller does not mention it'],
      ['The mandatory particulars of the online seller', '· identity, address and company number, all verifiable'],
      ['The payment method and your bank remedies', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on the Belgian Code of Economic Law. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your order the way it does.',
    bandLead: 'The AI analyses your case in 7 passes.',
    faqTitle: 'Your questions about undelivered purchases',
    faqs: [
      { q: 'Can I cancel and be refunded?', a: 'Yes: after 30 days with no delivery, you can demand rescission of the order and a full refund. Scan it first: you will know whether your order gives rise to a refund and how long you have.' },
      { q: 'Am I entitled to demand a refund?', a: 'Yes, it is a right. Claiming follows a precise procedure: Archer hands it to you, tailored to your order and your proof of purchase.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Letting it go may then be the right call, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to claim for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your case has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my order · Free',
    proof: {
      kicker: '● REFUND OWED',
      headline: 'The seller is in the wrong, not you.',
      summary: 'Your order was to be delivered within 30 days: 52 days have passed with no news. The right to rescind the contract is open, with a full refund.',
      kpis: [
        { label: 'Amount paid', sub: 'order not delivered' },
        { label: 'What you recover', sub: 'full refund' },
        { label: 'Deadline to act', value: 'Immediate', sub: 'legal deadline exceeded' },
      ],
      args: [
        { title: 'Delivery not made after 52 days', basis: 'Art. VI.43 CEL · obligation to deliver within 30 days', tag: 'STRONG' },
        { title: 'No reply from the seller to the reminders', basis: 'Art. VI.44 CEL · right to rescind the contract', tag: 'STRONG' },
        { title: 'No verifiable legal particulars on the site', basis: 'Art. VI.45 CEL · information obligation', tag: 'MODERATE' },
      ],
      letter: [
        'The formal notice to deliver within 8 days',
        'The request for rescission and a full refund',
        'The reference to the applicable articles of the CEL',
        'The bank chargeback procedure in case of silence',
      ],
      cta: 'Analyse my order · Free',
      projection: [
        { label: 'Refund obtained' },
        { label: 'Partial refund' },
        { label: 'No reply' },
      ],
      note: 'And if your order is still within the deadline, Archer tells you, no pointless step.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'abonnement-piege': {
    badge: '🔁 Subscription trap',
    h1: ['A free trial that bills you?', 'You may never have agreed to it.'],
    dropzone: 'Drop your statement or the email here, or click',
    checksTitle: 'What Archer checks on your subscription',
    checksIntro: 'One of these points alone can be enough to get your debits back',
    checks: [
      ['Your explicit consent to pay', '· a free trial is not an agreement to buy'],
      ['The prior information about renewal', '· mandatory, and often missing'],
      ['How easy cancelling really is', '· a cancellation made difficult is contestable'],
      ['The mandatory particulars of the renewal clause', '· in bold, in a separate box, on the first page'],
      ['The terms and conditions, accepted or not', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on the Belgian Code of Economic Law. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your subscription the way it does.',
    bandLead: 'The AI analyses your case in 7 passes.',
    faqTitle: 'Your questions about subscription traps',
    faqs: [
      { q: 'Can I cancel now?', a: 'Yes. And if your explicit agreement to pay cannot be demonstrated, you can also claim back the amounts already debited. Scan it first: you will know whether your subscription was validly concluded and how long you have.' },
      { q: 'Am I entitled to claim back what was debited?', a: 'Yes, it is a right. Claiming follows a precise procedure: Archer hands it to you, tailored to your subscription and your debits.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Letting it go may then be the right call, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to claim for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your debits keep going.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my subscription · Free',
    proof: {
      kicker: '● DEBIT CONTESTABLE',
      headline: 'You never agreed to this.',
      summary: 'A free trial turned into a paid subscription without your explicit agreement to pay being demonstrable, and the prior information about renewal appears nowhere.',
      kpis: [
        { label: 'Debited each month', sub: 'for 4 months' },
        { label: 'Recoverable', sub: 'total debited' },
        { label: 'Deadline to observe', value: '8 weeks', sub: 'per SEPA direct debit' },
      ],
      args: [
        { title: 'No explicit consent to pay after the trial', basis: 'Art. VI.45 CEL · express agreement to the payment obligation required', tag: 'STRONG' },
        { title: 'Prior information about renewal missing', basis: 'Book VI CEL · unfair commercial practices', tag: 'STRONG' },
        { title: 'Cancellation procedure made hard to reach', basis: 'Book VI CEL · unfair terms and practices', tag: 'MODERATE' },
      ],
      letter: [
        'The immediate cancellation of the subscription, free of charge',
        'The request for a refund of the amounts debited',
        'The request for proof of your consent to pay',
        'The revocation of the direct debit mandate with your bank',
      ],
      cta: 'Analyse my subscription · Free',
      projection: [
        { label: 'Refund obtained' },
        { label: 'Cancellation + gesture' },
        { label: 'Cancellation only' },
      ],
      note: 'And if you did agree, Archer tells you, and explains how to cancel properly, free of charge.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },

  'sinistre-refuse': {
    badge: '🛡️ Claim refused',
    h1: ['Refused with a form letter?', 'Have their refusal checked.'],
    dropzone: 'Drop the refusal letter here, or click',
    checksTitle: 'What Archer checks on your refusal letter',
    checksIntro: 'One of these defects alone can be enough to bring the refusal down',
    checks: [
      ['The reasons given for the refusal', '· they must be precise and documented, not a clause quoted'],
      ['The exclusion clause invoked', '· its validity and its actual application to your case'],
      ['The evidence the insurer brings', '· it is for them to prove the exclusion, not for you'],
      ['The mandatory particulars of the policy', '· exclusions must be exhaustively listed in it'],
      ['Your right to a counter-assessment', ''],
      ['The amount claimed', 'against the official scale'],
    ],
    legalNote: 'Every check rests on the Belgian Insurance Act. Every reference cited is verified.',
    aiTail: 'No generic chatbot reads your refusal letter the way it does.',
    bandLead: 'The AI analyses your case in 7 passes.',
    faqTitle: 'Your questions about insurance refusals',
    faqs: [
      { q: 'Are they allowed to refuse?', a: 'Only on an exclusion provided for in the contract, and it is for the insurer to prove it, not for you. Scan it first: you will know whether the refusal letter can be contested and how long you have.' },
      { q: 'Is contesting it legal?', a: 'Yes, it is a right. Contesting follows a precise procedure: Archer hands it to you, tailored to your refusal letter.' },
      { q: 'And if the other side’s case is solid?', a: 'Archer tells you straight. Letting it go may then be the right call, and you will not have paid a lawyer to find out.' },
      { q: 'How much does it cost?', a: 'The scan and the verdict: free, no card. If you want Archer to contest it for you: from 34€/month with a 12-month commitment, 39€ with none.' },
    ],
    finalCta: ['Your appeal has deadlines.', 'Every day counts.'],
    finalCtaBtn: 'Analyse my refusal · Free',
    proof: {
      kicker: '● REFUSAL CONTESTABLE',
      headline: 'The clause invoked does not apply.',
      summary: 'The insurer refuses your claim by invoking an exclusion for lack of maintenance, but no expert report backs it up. This refusal rests on an assertion, not on evidence.',
      kpis: [
        { label: 'Amount of the claim', sub: 'estimated value' },
        { label: 'What you recover', sub: 'if it succeeds' },
        { label: 'Deadline to contest', value: '1 month', sub: 'before the limitation period' },
      ],
      args: [
        { title: 'Refusal not supported by a documented expert assessment', basis: 'Act of 4 April 2014, art. 62 · duty to state reasons', tag: 'STRONG' },
        { title: 'No evidence of the defect invoked', basis: 'Art. 1315 former Civil Code · the burden of proof lies with the insurer', tag: 'STRONG' },
        { title: 'Handling time for the file exceeded', basis: 'Art. 22 Act of 4 April 2014 · reasonable time', tag: 'MODERATE' },
      ],
      letter: [
        'The challenge to the refusal and the request for the expert report',
        'The request for an independent counter-assessment',
        'The formal notice to re-examine the file',
        'The reminder of the avenues of appeal (insurance ombudsman)',
      ],
      cta: 'Analyse my refusal · Free',
      projection: [
        { label: 'Compensation obtained' },
        { label: 'Partial compensation' },
        { label: 'Refusal upheld' },
      ],
      note: 'And if the refusal is well founded, Archer explains it clearly, so you know where you stand.',
      foot: ['ANALYSIS GENERATED IN 58 SECONDS', '4 VERIFIED LEGAL REFERENCES · BELGIAN LAW'],
      juristPrice: 'From 34 €/month',
    },
  },
};

export default LANDING_THEMES_EN;
